import { DayjsUnit } from '@/models/Dayjs.ts';
import { PVSUtilStrategy } from '@/models/PVSStrategy.ts';
import { Appointment } from '@/models/appointment/Appointment.ts';
import { UpdateAppointmentDto } from '@/models/appointment/UpdateAppointmentDto.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';
import { UpdatableDataCurEvent } from '@/models/data-cur/dc-event-dto.ts';

import { buildDetailsString } from '@/utils/appointmentUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';

export const data_cur_util_strategy: PVSUtilStrategy = {
    getPvsPatientId: (appointment: Appointment) => appointment.data_cur_links?.patient_id,
    getScheduleTypeId: (appointment: Appointment) => appointment.data_cur_links?.event_type_id,
    getCalendarId: (appointment: Appointment) => appointment.data_cur_links?.resource_id,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getProfessionalId: (appointment: Appointment) => {
        // TODO: PVS-MIGRATION implement when available
        // there is no professional id in data cur TBD
        return undefined as string | undefined;
    },
    getInstanceId: (appointment: Appointment) => appointment.data_cur_links?.instance_id,
    getRoomId: (appointment: Appointment) => appointment.data_cur_links?.room_id,
    updateRoomId: (appointment: Appointment, room_id: string): Appointment => {
        // Create a copy of the appointment to avoid mutating the original
        const updated_appointment = { ...appointment };

        // Ensure data_cur_links object exists
        if (updated_appointment.data_cur_links) {
            updated_appointment.data_cur_links = { ...updated_appointment.data_cur_links, room_id };
        }

        return updated_appointment;
    },

    getUpdatePartialDtoPVSAppointment: (
        update_data: Partial<UpdateAppointmentDto>,
        appointment: Appointment
    ): UpdatableDataCurEvent => {
        // Room and priority are not available in data cur

        // Error if not data cur
        if (appointment.pvs_provider !== PVSProvider.DATA_CUR) throw new Error('Appointment is not data cur');

        if (
            !appointment.data_cur_links ||
            !appointment.data_cur_links.instance_id ||
            !appointment.data_cur_links.event_id ||
            !appointment.data_cur_links.event_type_code ||
            !appointment.data_cur_links.resource_id ||
            !appointment.data_cur_links.patient_id
        ) {
            throw new Error('Appointment is not data cur');
        }

        const body: UpdatableDataCurEvent = {
            instance_id: appointment.data_cur_links.instance_id,
            id: appointment.data_cur_links.event_id,
            resource_id: appointment.data_cur_links.resource_id,
            patient_id: appointment.data_cur_links.patient_id,
            name: appointment.name,
            description: appointment.data.info,
            memo: appointment.description,
            start: appointment.start,
            end: appointment.end,
        };

        if (update_data.appointment?.description !== undefined) body.memo = update_data.appointment.description;
        if (update_data.appointment?.start) body.start = update_data.appointment.start;
        if (update_data.appointment?.start || update_data.appointment?.duration) {
            body.end = dayjs(body.start)
                .add(update_data.appointment?.duration?.value ?? appointment.duration.value, DayjsUnit.MINUTE)
                .toISOString();
        }

        if (update_data.professional) {
            body.resource_id = update_data.professional.calendar_id;
        }

        if (update_data.pvs?.data_cur?.event_type_code) {
            body.name = update_data.pvs.data_cur.event_type_code;
        }

        if (update_data.appointment || update_data.professional) {
            body.description = buildDetailsString(
                undefined,
                undefined,
                update_data.appointment?.types ?? appointment.data.services,
                update_data.appointment?.info ?? appointment.data.info,
                undefined,
                update_data.appointment?.ticket,
                undefined,
                update_data.appointment?.is_no_show
            );
        }

        if (update_data.patient_id) {
            body.patient_id = update_data.patient_id;
        }

        return body;
    },
};
